// frontend/src/api/client.js
const BASE = process.env.REACT_APP_API_URL || '';

async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  // 401 on /auth/me is expected when not logged in — return null, don't redirect
  if (res.status === 401) return null;

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }

  if (res.status === 204) return null;
  return res.json();
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const getMe  = () => apiFetch('/api/auth/me');
export const logout = () => { window.location.href = `${BASE}/api/auth/logout`; };

// Redirect to backend Azure login (proxied to the App Service under /api)
export const loginWithAzure = () => { window.location.href = `${BASE}/api/auth/login`; };

// ─── Rep dashboard ────────────────────────────────────────────────────────────
// Build a query string from an object, dropping null/undefined/'' values.
const qs = (params) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
};

// `repId` is honored by the backend only for a global_admin (to view any AE's
// dashboard); it's ignored for every other role. Omit it for normal self-view.
export const getRepSummary = (quarter, repId) => apiFetch(`/api/reps/me/summary${qs({ quarter, repId })}`);
export const getRepPayouts = (quarter, repId) => apiFetch(`/api/reps/me/payouts${qs({ quarter, repId })}`);
export const getRepOrders  = (params = {})    => apiFetch(`/api/reps/me/orders${qs(params)}`);
export const getRepPending = (repId)          => apiFetch(`/api/reps/me/pending${qs({ repId })}`);

// ─── My Calculator (rep dashboard) ────────────────────────────────────────────
// "My Math" — the payout arithmetic, step by step, for every month in the quarter.
export const getRepMath = (quarter, repId) => apiFetch(`/api/reps/me/math${qs({ quarter, repId })}`);
// "My Sandbox" — replay the quarter with hypothetical GP layered on. Read-only:
// `adds` is { "YYYY-MM": { hwSwGp, servicesGp, fodGp } } and nothing is persisted.
export const runRepSandbox = (quarter, adds, repId) => apiFetch(
  `/api/reps/me/sandbox${qs({ repId })}`,
  { method: 'POST', body: JSON.stringify({ quarter, adds }) },
);

// ─── Finance / Admin ──────────────────────────────────────────────────────────
export const getAllPayouts    = (params = {}) => apiFetch(`/api/payouts?${new URLSearchParams(params)}`);
export const getTeamRevenue   = (quarter)     => apiFetch(`/api/payouts/team-revenue?quarter=${encodeURIComponent(quarter)}`);
// Booking basis (closed-won by actual close date) — Manager "My Dashboard" only.
export const getTeamBookings  = (quarter)     => apiFetch(`/api/payouts/team-bookings?quarter=${encodeURIComponent(quarter)}`);
// Company-wide shipped GP by category (fulfillment basis) — Finance dashboard.
export const getTeamShippedByCategory = (quarter) => apiFetch(`/api/payouts/team-shipped-by-category?quarter=${encodeURIComponent(quarter)}`);
// All reps' pending (held-back) shipments — Finance dashboard.
export const getPendingAll    = ()            => apiFetch('/api/admin/pending');
// Distinct AEs with shipped-line data — global admin's "View as AE" picker.
export const getReps          = ()            => apiFetch('/api/admin/reps');
// Two-level approval: review (Level 1) → approve (Level 2, final). Unapprove sends
// a reviewed or approved payout back to pending. Who may call each is enforced
// server-side against the approver lists on Finance → Settings.
export const reviewPayout    = (repId, month) => apiFetch(`/api/payouts/${repId}/${month}/review`, { method: 'POST' });
export const approvePayout   = (repId, month) => apiFetch(`/api/payouts/${repId}/${month}/approve`, { method: 'POST' });
export const unapprovePayout = (repId, month) => apiFetch(`/api/payouts/${repId}/${month}/unapprove`, { method: 'POST' });
export const exportPayoutsCsv = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  window.location.href = `${BASE}/api/payouts/export.csv${q ? `?${q}` : ''}`;
};
export const getUsers     = ()           => apiFetch('/api/admin/users');

// Departments (sales / services / operations / presales / finance)
export const getDepartments = ()     => apiFetch('/api/departments');
export const getDepartment  = (code) => apiFetch(`/api/departments/${encodeURIComponent(code)}`);

// Leadership plan assignments (per user): Sales manager comp / Director of Operations
export const getUserPlans    = ()               => apiFetch('/api/admin/user-plans');
export const updateUserPlans = (userId, assignments) => apiFetch(`/api/admin/user-plans/${userId}`, { method: 'PUT', body: JSON.stringify({ assignments }) });

// Finance department dashboard — commissions by department for a quarter
export const getCommissionsByDepartment = (quarter) => apiFetch(`/api/payouts/by-department?quarter=${encodeURIComponent(quarter)}`);

// Register-based leadership plans: head_of_sales | presales_director | solutions_architect
export const getLeadershipPlan        = (plan, quarter)       => apiFetch(`/api/leadership/${plan}/${encodeURIComponent(quarter)}`);
export const updateLeadershipSettings = (plan, quarter, data) => apiFetch(`/api/leadership/${plan}/${encodeURIComponent(quarter)}/settings`, { method: 'PUT', body: JSON.stringify(data) });

// Director of Operations plan (Operations department)
export const getOpsPlan            = (quarter)       => apiFetch(`/api/ops-plan/${encodeURIComponent(quarter)}`);
export const refreshOpsPlan        = (quarter)       => apiFetch(`/api/ops-plan/${encodeURIComponent(quarter)}/refresh`, { method: 'POST' });
export const updateOpsPlanSettings = (quarter, data) => apiFetch(`/api/ops-plan/${encodeURIComponent(quarter)}/settings`, { method: 'PUT', body: JSON.stringify(data) });
export const addOpsRock            = (quarter, data) => apiFetch(`/api/ops-plan/${encodeURIComponent(quarter)}/rocks`, { method: 'POST', body: JSON.stringify(data) });
export const updateOpsRock         = (id, data)      => apiFetch(`/api/ops-plan/rocks/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteOpsRock         = (id)            => apiFetch(`/api/ops-plan/rocks/${id}`, { method: 'DELETE' });
export const resetOpsRockStatus     = (id)            => apiFetch(`/api/ops-plan/rocks/${id}`, { method: 'PATCH', body: JSON.stringify({ resetStatus: true }) });
// Comp plan assignments (which plan pays a rep's deals, by Actual Close Date).
export const getPlanAssignments    = ()                     => apiFetch('/api/admin/plans');
export const updatePlanAssignments = (repId, assignments)   => apiFetch(`/api/admin/plans/${encodeURIComponent(repId)}`, { method: 'PUT', body: JSON.stringify({ assignments }) });
export const updateUser   = (id, data)   => apiFetch(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const getAuditLog  = ()           => apiFetch('/api/admin/audit-log');
export const getSyncState = ()           => apiFetch('/api/admin/sync-state');
export const triggerSync  = (body = {})  => apiFetch('/api/admin/sync', { method: 'POST', body: JSON.stringify(body) });
export const getCompPlan  = ()           => apiFetch('/api/admin/comp-plan');

// ─── Settings (per-quarter quotas) ──────────────────────────────────────────────
export const getQuotas    = (quarter)    => apiFetch(`/api/settings/quotas?quarter=${encodeURIComponent(quarter)}`);
export const updateQuotas = (data)       => apiFetch('/api/settings/quotas', { method: 'PUT', body: JSON.stringify(data) });

// ─── Settings (payout approvers — global admin writes, finance reads) ──────────
export const getApprovers    = ()        => apiFetch('/api/settings/approvers');
export const updateApprovers = (data)    => apiFetch('/api/settings/approvers', { method: 'PUT', body: JSON.stringify(data) });
// Email notifications — status for the Settings card; test send goes to the caller.
export const getNotificationStatus = ()  => apiFetch('/api/settings/notifications');
export const sendTestNotification  = ()  => apiFetch('/api/settings/notifications/test', { method: 'POST', body: JSON.stringify({}) });
